import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Section = ({ title, icon, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border rounded">
            <div
                className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#DFE4EB", borderRadius: "4px 4px 0 0" }}
                onClick={() => setOpen(!open)}
            >
                <span><i className={`bi ${icon} me-2`}></i>{title}</span>
                <span>{open ? "▾" : "▸"}</span>
            </div>
            {open && <div className="row g-3 p-3">{children}</div>}
        </div>
    );
};

const ALLOCATION_TYPES = ["Permanent", "Temporary", "Project-based"];
const CONDITION_OPTIONS = ["New", "Good", "Fair", "Damaged"];

export default function EditAllocation() {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [original, setOriginal] = useState(null);
    const [errors, setErrors] = useState({});

    /* Form fields that are editable */
    const [formData, setFormData] = useState({
        allocationType: "Permanent",
        allocationDate: "",
        expectedReturnDate: "",
        conditionAtIssue: "Good",
        handoverRemarks: "",
        status: "ACTIVE",
    });

    /* Read-only display info loaded from the record */
    const [displayInfo, setDisplayInfo] = useState({
        employeeName: "", employeeId: "", department: "", designation: "",
        assetCode: "", assetName: "", category: "", serialNumber: "",
    });

    const [activeTab, setActiveTab] = useState("systemNotes");
    const [visitedTabs, setVisitedTabs] = useState(new Set(["systemNotes"]));
    const [navbarColor, setNavbarColor] = useState("#080235");

    useEffect(() => {
        const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
        const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
        return () => { bl.remove(); ic.remove(); };
    }, []);

    useEffect(() => {
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
        // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
    }, [navigate, token]);

    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
    }, [token]);

    /* Load allocation by fetching list and finding by id */
    useEffect(() => {
        if (!token || !id) return;
        setLoading(true);
        fetch(`${API_BASE_URL}/api/asset-allocations/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => { if (!r.ok) throw new Error("Not found"); return r.json(); })
            .then(found => {
                setOriginal(found);
                setFormData({
                    allocationType: found.allocationType || "Permanent",
                    allocationDate: found.allocationDate || "",
                    expectedReturnDate: found.expectedReturnDate || "",
                    conditionAtIssue: found.conditionAtIssue || "Good",
                    handoverRemarks: found.handoverRemarks || "",
                    status: found.status || "ACTIVE",
                });
                setDisplayInfo({
                    employeeName: found.employeeName || "",
                    assetCode: found.assetCode || "",
                    assetName: found.assetName || "",
                    category: found.category || "",
                    serialNumber: found.serialNumber || "",
                });
            })
            .catch(() => { toast.error("Failed to load allocation."); navigate("/allallocations"); })
            .finally(() => setLoading(false));
    }, [id, token, navigate]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(p => ({ ...p, [name]: value }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: "" }));
    };

    const validate = () => {
        const e = {};
        if (!formData.allocationType) e.allocationType = "Allocation type is required.";
        if (!formData.allocationDate) e.allocationDate = "Allocation date is required.";
        if (formData.allocationType === "Temporary" && !formData.expectedReturnDate)
            e.expectedReturnDate = "Return date is required for temporary allocation.";
        return e;
    };

    const handleSubmit = async (e) => {
        e && e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error("Please fix the errors before saving.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/asset-allocations/${id}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || "Failed to update allocation.");
            }
            toast.success("Allocation updated successfully.");
            setTimeout(() => navigate("/allallocations"), 1200);
        } catch (err) {
            toast.error(err.message || "Failed to update allocation.");
        } finally {
            setSubmitting(false);
        }
    };

    const isActive = original?.status === "ACTIVE";

    if (loading) return (
        <>
            <div className="fixed-top"><AssetModuleNavbar /></div>
            <div className="container-fluid" style={{ paddingTop: 110 }}>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary"></div>
                    <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading allocation...</div>
                </div>
            </div>
        </>
    );

    return (
        <>
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="fixed-top"><AssetModuleNavbar /></div>
            <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <h4 className="fw-bold mb-0">
                                <i className="bi bi-pencil-square me-2"></i>Edit Allocation
                            </h4>
                            <span
                                className="badge"
                                style={{
                                    background: isActive ? "#dcfce7" : "#f3f4f6",
                                    color: isActive ? "#198754" : "#6b7280",
                                    border: `1px solid ${isActive ? "#86efac" : "#d1d5db"}`,
                                    fontSize: 12,
                                }}
                            >
                                {original?.status}
                            </span>
                        </div>
                        <div className="d-flex gap-2">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSubmit}
                            disabled={submitting}
                        >
                            {submitting
                                ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                                : <>Save </>}
                        </button>
                        
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => navigate(-1)}
                        >
                             Cancel
                        </button>
                    </div>
                    </div>
                    
                </div>

                <form onSubmit={handleSubmit}>

                    {/* Employee — read only */}
                    <Section title="Employee (Read-only)" icon="bi-person">
                        <div className="col-md-3">
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: "#6b7280" }}>Employee Name</label>
                            <input className="form-control form-control-sm bg-light" readOnly value={displayInfo.employeeName} />
                        </div>
                    </Section>

                    {/* Asset — read only */}
                    <Section title="Asset (Read-only)" icon="bi-laptop">
                        <div className="col-md-2">
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: "#6b7280" }}>Asset Code</label>
                            <input className="form-control form-control-sm bg-light" readOnly value={displayInfo.assetCode} style={{ fontFamily: "monospace" }} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: "#6b7280" }}>Asset Name</label>
                            <input className="form-control form-control-sm bg-light" readOnly value={displayInfo.assetName} />
                        </div>
                    </Section>

                    {/* Editable allocation details */}
                    <Section title="Allocation Details" icon="bi-info-circle">
                        <div className="col-md-3">
                            <label className="form-label">
                                Allocation Type <span className="text-danger">*</span>
                            </label>
                            <select
                                name="allocationType"
                                className={`form-select form-select-sm ${errors.allocationType ? "is-invalid" : ""}`}
                                value={formData.allocationType}
                                onChange={handleChange}
                            >
                                {ALLOCATION_TYPES.map(o => <option key={o}>{o}</option>)}
                            </select>
                            {errors.allocationType && <div className="invalid-feedback">{errors.allocationType}</div>}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">
                                Allocation Date <span className="text-danger">*</span>
                            </label>
                            <input
                                type="date"
                                name="allocationDate"
                                className={`form-control form-control-sm ${errors.allocationDate ? "is-invalid" : ""}`}
                                value={formData.allocationDate}
                                onChange={handleChange}
                            />
                            {errors.allocationDate && <div className="invalid-feedback">{errors.allocationDate}</div>}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">
                                Expected Return Date
                                {formData.allocationType === "Temporary" && <span className="text-danger"> *</span>}
                            </label>
                            <input
                                type="date"
                                name="expectedReturnDate"
                                className={`form-control form-control-sm ${errors.expectedReturnDate ? "is-invalid" : ""}`}
                                value={formData.expectedReturnDate}
                                onChange={handleChange}
                                min={formData.allocationDate || undefined}
                            />
                            {errors.expectedReturnDate && <div className="invalid-feedback">{errors.expectedReturnDate}</div>}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Condition at Issue</label>
                            <select
                                name="conditionAtIssue"
                                className="form-select form-select-sm"
                                value={formData.conditionAtIssue}
                                onChange={handleChange}
                            >
                                {CONDITION_OPTIONS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Status</label>
                            <select
                                name="status"
                                className="form-select form-select-sm"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="RETURNED">RETURNED</option>
                            </select>
                        </div>

                        <div className="col-md-12">
                            <label className="form-label">Handover Remarks</label>
                            <textarea
                                name="handoverRemarks"
                                className="form-control form-control-sm"
                                rows={3}
                                value={formData.handoverRemarks}
                                onChange={handleChange}
                                placeholder="Physical condition notes, accessories included, etc..."
                                maxLength={500}
                            />
                            <div className="text-end" style={{ fontSize: 11, color: "#9ca3af" }}>
                                {formData.handoverRemarks.length}/500
                            </div>
                        </div>
                    </Section>

                    {/* Return details — shown only if switching to RETURNED */}
                    {formData.status === "RETURNED" && original?.status === "ACTIVE" && (
                        <Section title="Return Details" icon="bi-arrow-return-left">
                            <div className="col-12">
                                <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 13 }}>
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Setting status to <strong>RETURNED</strong> will mark this asset as returned and restore it to Available. Use the dedicated Return flow on the View page for full return processing with condition and remarks.
                                </div>
                            </div>
                        </Section>
                    )}

                    {/* System Information Tabs */}
                    <ul className="nav nav-tabs mb-0 fw-semibold mt-4" style={{ backgroundColor: navbarColor }}>
                        {[["systemNotes", "System Notes"]].map(([key, label]) => (
                            <li className="nav-item" key={key}>
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === key ? "active" : ""}`}
                                    onClick={() => {
                                        setActiveTab(key);
                                        setVisitedTabs(prev => new Set(prev).add(key));
                                    }}
                                    style={{
                                        color: activeTab === key ? navbarColor : "#fff",
                                        backgroundColor: activeTab === key ? "#fff" : "transparent",
                                        fontWeight: activeTab === key ? "600" : "normal",
                                    }}
                                >
                                    {label}
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="border p-3 bg-white mb-4">
                        <div className="text-muted small mb-2">
                            <strong>Date Created:</strong>{" "}
                            {original?.createdAt ? new Date(original.createdAt).toLocaleString() : "—"}
                        </div>
                        {visitedTabs.has("systemNotes") && (
                            <div style={{ display: activeTab === "systemNotes" ? "block" : "none" }}>
                                <SystemNotes entityType="ALLOCATION" entityId={id} token={token} />
                            </div>
                        )}
                    </div>

                    <div className="d-flex gap-2 mt-3 mb-5">
                        <button className="btn btn-primary btn-sm" type="submit" disabled={submitting}>
                            {submitting
                                ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                                : <>Save </>}
                        </button>
                       
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}
